<template>
  <v-row style="border: 1px solid gray; margin-bottom: 30px">
    <v-col cols="12">
      <div>
        <h1>Seed PF Adviser Regional Data</h1>
        <h3>
          Upload the PF Advisers list to populate advisers' regional fields
          (channel, region, area, team, employment status).
        </h3>
        <p>
          Advisers are matched on their advisor code or omipay code. If a code
          appears more than once in the file, the last occurrence is used.
        </p>
        <p>
          The first row must hold the column headers, in any order: Intermediary
          Code, Channel, Region, Area, Team. Optional: Employment Status, Email
          (used only when the code matches no adviser). Other columns are
          ignored.
        </p>
        <v-file-input
          v-model="file"
          single-line
          outlined
          dense
          filled
          :loading="buttonLoading"
          label="Upload file"
          class="file-upload-btn"
        />
      </div>
      <v-btn
        rounded
        depressed
        :loading="buttonLoading"
        :disabled="!file"
        color="primary"
        @click="uploadFile()"
        ><span>Upload</span></v-btn
      >
    </v-col>

    <v-col cols="12" v-if="isProcessing">
      <p>Processing file, please wait…</p>
      <v-progress-linear indeterminate color="primary" />
    </v-col>

    <v-col cols="12" v-if="isFailed">
      <p style="color: red">Something went wrong: {{ job.errorMessage }}</p>
    </v-col>

    <v-col cols="12" v-if="seedComplete">
      <p style="color: green">
        Done. {{ matchedCount }} advisers were updated with their regional data.
      </p>
    </v-col>

    <v-col v-if="nothingToSeed" cols="12">
      <p>0 advisers matched. Nothing to seed.</p>
    </v-col>

    <v-col cols="12" v-if="showPreview">
      <h3 class="mb-2">
        {{ matchedCount }} adviser(s) matched and will be updated.
        <span v-if="unmatchedCount"
          >({{ unmatchedCount }} row(s) in the file matched no adviser.)</span
        >
      </h3>
      <v-data-table
        :headers="headers"
        :items="tableItems"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        class="elevation-1"
        dense
      />
      <v-btn
        rounded
        depressed
        class="mt-4"
        color="primary"
        :loading="seedLoading"
        :disabled="matchedCount === 0"
        @click="seed()"
        ><span>Seed {{ matchedCount }} adviser(s)</span></v-btn
      >
    </v-col>
  </v-row>
</template>

<script>
  import {
    UPLOAD_PF_ADVISER_SEED_MUTATION,
    POLL_PF_ADVISER_SEED_STATUS_MUTATION,
    APPLY_PF_ADVISER_SEED_CHANGES_MUTATION,
  } from '../../constants/graphql/daily-tasks';

  export default {
    name: 'SeedPfAdviserRegionalData',
    data() {
      return {
        file: null,
        buttonLoading: false,
        seedLoading: false,
        seedComplete: false,
        intervalId: null,
        job: null,
        headers: [
          { text: 'Adviser', value: 'name' },
          { text: 'Intermediary code', value: 'code' },
          { text: 'Channel', value: 'channel' },
          { text: 'Region', value: 'region' },
          { text: 'Area', value: 'area' },
          { text: 'Team', value: 'team' },
          { text: 'Employment status', value: 'status' },
        ],
      };
    },
    computed: {
      isProcessing() {
        return this.job && this.job.status === 'processing';
      },
      isFailed() {
        return this.job && this.job.status === 'failed';
      },
      isCompleted() {
        return this.job && this.job.status === 'completed';
      },
      showPreview() {
        return (
          this.isCompleted && !this.seedComplete && this.tableItems.length > 0
        );
      },
      nothingToSeed() {
        return (
          this.isCompleted && !this.seedComplete && this.tableItems.length === 0
        );
      },
      matchedCount() {
        return this.job ? this.job.matchedCount : 0;
      },
      unmatchedCount() {
        return this.job ? this.job.unmatchedCount : 0;
      },
      tableItems() {
        if (!this.job || !this.job.pfAdviserSeedActions) return [];
        return this.job.pfAdviserSeedActions.map(action => ({
          name: action.user ? action.user.fullName : '',
          code: action.intermediaryCode,
          channel: action.advisorChannel,
          region: action.advisorRegion,
          area: action.advisorArea,
          team: action.advisorTeam,
          status: action.advisorEmploymentStatus,
        }));
      },
    },
    created() {
      this.startPolling();
    },
    methods: {
      uploadFile() {
        if (!this.file) return;
        this.buttonLoading = true;
        this.seedComplete = false;
        const reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = function (evt) {
          this.uploadFileToServer(evt.target.result);
        }.bind(this);
      },
      uploadFileToServer(fileBase64) {
        const testatorId = this.$store.getters.testatorId;
        this.$apollo
          .mutate({
            mutation: UPLOAD_PF_ADVISER_SEED_MUTATION,
            variables: { testatorId, fileBase64 },
          })
          .then(response => {
            this.job = response.data.uploadPfAdviserSeed;
            this.buttonLoading = false;
            this.file = null;
            this.startPolling();
          })
          .catch(() => {
            this.buttonLoading = false;
            this.file = null;
          });
      },
      startPolling() {
        this.stopPolling();
        this.pollStatus();
        this.intervalId = setInterval(this.pollStatus, 5000);
      },
      stopPolling() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      },
      pollStatus() {
        const testatorId = this.$store.getters.testatorId;
        this.$apollo
          .mutate({
            mutation: POLL_PF_ADVISER_SEED_STATUS_MUTATION,
            variables: { testatorId },
          })
          .then(response => {
            this.job = response.data.pollPfAdviserSeedStatus;
            if (this.job.status !== 'processing') this.stopPolling();
          })
          .catch(() => {
            this.stopPolling();
          });
      },
      seed() {
        if (!this.job) return;
        this.seedLoading = true;
        const testatorId = this.$store.getters.testatorId;
        this.$apollo
          .mutate({
            mutation: APPLY_PF_ADVISER_SEED_CHANGES_MUTATION,
            variables: { testatorId, pfAdviserSeedJobId: this.job.id },
          })
          .then(() => {
            this.seedLoading = false;
            this.seedComplete = true;
            this.stopPolling();
          })
          .catch(() => {
            this.seedLoading = false;
          });
      },
    },
    beforeDestroy() {
      this.stopPolling();
    },
  };
</script>
